import { Locale, View } from 'ckeditor5';
import { CommentThreadView, User } from 'ckeditor5-premium-features';
import { DocumentStatus, QualioDocument } from '../../api/model/document';
import { handleRemoveCommentThread } from '../../config/handlers';

interface Comment {
  author: User;
}

export default class QualioCommentThreadView extends CommentThreadView {
  static permission: number;
  static qualioDocument: QualioDocument;
  static useMultiRootEditor: boolean;

  private user: User;
  private commentThread: any;

  constructor(
    locale: Locale,
    commentThread: CommentsRepository.CommentThreadToDisplay,
    user: User,
    config: any,
  ) {
    super(locale, commentThread as any, user, config);
    this.commentThread = commentThread;
    this.user = user;
    this.commentThreadInputView.commentInputView.showButtons = true;
    if (!QualioCommentThreadView.useMultiRootEditor) {
      this.addCustomThreadResolveButton();
    }

    this.on('change:isActive', () => {
      if (!QualioCommentThreadView.useMultiRootEditor) {
        this.addCustomThreadResolveButton();
      }
      const submitLabel = this.isNewThread() ? 'Reply' : 'Comment';
      this.commentThreadInputView.commentInputView.cancelButtonView.set({
        label: 'Cancel',
        withText: true,
        icon: null,
      });
      this.commentThreadInputView.commentInputView.submitButtonView.set({
        label: submitLabel,
        withText: true,
        icon: null,
      });
    });
  }

  private addCustomThreadResolveButton() {
    this.resolveButton.set({
      isVisible: false,
    });
    if (
      this.commentsListView?.commentViews.first &&
      this.shouldDisplayResolveButton(this.user)
    ) {
      const { removeButton } = this.commentsListView?.commentViews
        ?.first as any;
      if (removeButton) {
        removeButton.set({
          isEnabled: true,
          isVisible: true,
          tooltip: true,
          label: 'Mark as resolved',
        });
        removeButton.off('execute');
        removeButton.on('execute', () => {
          this.fire('removeCommentThread');
          handleRemoveCommentThread(
            this.commentThread.channelId,
            this.commentThread.id,
            QualioCommentThreadView.qualioDocument.company_id,
            QualioCommentThreadView.qualioDocument.id,
          );
        });
      }
    }
    this.hideEditButton();
  }

  private hideEditButton() {
    if (!this.isCommentThreadEditable()) {
      const commentsViews = this.commentsListView?.commentViews;
      commentsViews?.map((commentView: View) => {
        const { editButton } = commentView as any;
        if (editButton) {
          editButton.set({
            isEnabled: false,
            isVisible: false,
          });
        }
        return commentView;
      });
    }
  }

  private isNewThread() {
    return this.commentsListView.length > 0;
  }

  private shouldDisplayResolveButton = (user: User) => {
    const { id: currentUserEmail } = user;
    const commentsArr: Comment[] = Array.from(this.commentThread.comments);
    const [commentThreadStart] = commentsArr;

    if (!commentThreadStart) {
      return false;
    }

    const { author: commentThreadAuthor } = commentThreadStart;

    if (!this.isCommentThreadEditable()) {
      return false;
    }

    const isCommentAuthor = user.id === commentThreadAuthor?.id;
    const isDocumentOwner =
      QualioCommentThreadView.qualioDocument.owner.email === currentUserEmail;

    return isCommentAuthor || isDocumentOwner;
  };

  private isCommentThreadEditable = () => {
    return (
      QualioCommentThreadView.qualioDocument.status_id ===
        DocumentStatus.Draft ||
      QualioCommentThreadView.qualioDocument.status_id ===
        DocumentStatus.For_Review
    );
  };
}

export const qualioCommentThreadBuilder = (
  commentsPermission: number,
  qualioDocument: QualioDocument,
  useMultiRootEditor = false,
) => {
  QualioCommentThreadView.permission = commentsPermission;
  QualioCommentThreadView.qualioDocument = qualioDocument;
  QualioCommentThreadView.useMultiRootEditor = useMultiRootEditor;
  return QualioCommentThreadView;
};
